import type { RefObject } from 'react';
import React, { useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import type Immutable from 'immutable';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    LexicalTypeaheadMenuPlugin,
    MenuOption,
    useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import type { TextNode } from 'lexical';

import type { CommentablePersonProfile } from 'js/src/client';
import { $createMentionNode } from 'js/src/components/common/comments/MentionNode';
import type { ImmutableMap } from 'js/src/libs/consts';

class MentionTypeaheadOption extends MenuOption {
    person: ImmutableMap<CommentablePersonProfile>;

    constructor(person: ImmutableMap<CommentablePersonProfile>) {
        super(person.get('first_name') + ' ' + person.get('last_name'));
        this.person = person;
    }
}

const convertPeopleToMentionableTypeAheadOption = (
    people: Immutable.List<ImmutableMap<CommentablePersonProfile>>,
): MentionTypeaheadOption[] => {
    const results: MentionTypeaheadOption[] = [];
    people.forEach((person) => {
        results.push(new MentionTypeaheadOption(person));
    });
    return results;
};

function MentionMenuItem({
    index,
    isSelected,
    onClick,
    onMouseEnter,
    option,
}: {
    index: number;
    isSelected: boolean;
    onClick: () => void;
    onMouseEnter: () => void;
    option: MentionTypeaheadOption;
}) {
    let className = 'mention-menu-item';
    if (isSelected) {
        className += ' selected';
    }
    return (
        <li
            key={option.person.get('id')}
            tabIndex={-1}
            className={className}
            role="option"
            aria-selected={isSelected}
            id={'typeahead-item-' + index}
            onMouseEnter={onMouseEnter}
            onClick={onClick}
        >
            <span className="flex-40">{`${option.person.get('first_name')} ${option.person.get('last_name')}`}</span>
            <span className="flex-60">{`${option.person.get('email')}`}</span>
        </li>
    );
}

type Props = {
    people: Immutable.List<ImmutableMap<CommentablePersonProfile>>;
    menuContainerRef: RefObject<HTMLDivElement>;
};
const MentionsPlugin: React.FC<Props> = React.memo(function MentionsPlugin({
    people,
    menuContainerRef,
}) {
    const [editor] = useLexicalComposerContext();
    const [queryString, setQueryString] = React.useState<string | null>(null);
    const [queryResults, setQueryResults] = React.useState<MentionTypeaheadOption[]>([]);

    useEffect(() => {
        if (!people || people.size === 0) {
            setQueryResults([]);
        } else {
            const lowerCaseQueryString = queryString === null ? '' : queryString.toLowerCase();
            const queryPeopleMatches = people.filter((person) => {
                return (
                    person.get('first_name').toLowerCase().startsWith(lowerCaseQueryString) ||
                    person.get('email').toLowerCase().startsWith(lowerCaseQueryString)
                );
            });
            setQueryResults(convertPeopleToMentionableTypeAheadOption(queryPeopleMatches));
        }
    }, [queryString, people]);

    const triggerFn = useBasicTypeaheadTriggerMatch('@', { minLength: 0 });

    const onSelectOption = useCallback(
        (
            selectedOption: MentionTypeaheadOption,
            nodeToReplace: TextNode | null,
            closeMenu: () => void,
        ) => {
            editor.update(() => {
                const node = $createMentionNode(selectedOption.person);
                if (nodeToReplace) {
                    nodeToReplace.replace(node);
                }
                node.select();
                closeMenu();
            });
        },
        [editor],
    );

    return (
        <LexicalTypeaheadMenuPlugin
            onQueryChange={setQueryString}
            onSelectOption={onSelectOption}
            options={queryResults}
            triggerFn={triggerFn}
            menuRenderFn={(
                anchorElement,
                { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
            ) => {
                let listItems = null;
                if (queryResults && queryResults.length > 0) {
                    listItems = queryResults.map((option, index) => {
                        return (
                            <MentionMenuItem
                                index={index}
                                isSelected={selectedIndex === index}
                                key={option.person.get('id') || option.person.get('email')}
                                option={option}
                                onClick={() => {
                                    setHighlightedIndex(index);
                                    selectOptionAndCleanUp(option);
                                }}
                                onMouseEnter={() => {
                                    setHighlightedIndex(index);
                                }}
                            />
                        );
                    });
                }
                if (!menuContainerRef.current) {
                    return null;
                }
                return ReactDOM.createPortal(
                    <ul className="mentions-menu m-l5-0 p-l5-0">{listItems}</ul>,
                    menuContainerRef.current,
                );
            }}
        />
    );
});

export default MentionsPlugin;
